<template>
  <div :class="classes(n())">
    <HalfScreenDialog
      v-model:visible="currentVisible"
      :visible.sync="currentVisible"
      :class="n('dialog')"
      :title="title"
      :show-close="showClose"
      :scrollable="false"
      :close-on-click-overlay="closeOnClickOverlay"
      :lock-scroll="lockScroll"
      :destroy-on-close="destroyOnClose"
      :teleport="teleport"
      :actions="confirmActions"
      @close="handleCancel"
      @action-click="handleConfirm"
      @click-overlay="handleOverlayClick"
    >
      <!-- 自定义整个 toolbar -->
      <template
        v-if="$slots.toolbar"
        #header
      >
        <slot name="toolbar" />
      </template>

      <!-- 自定义标题 -->
      <template
        v-if="$slots.title"
        #header-center
      >
        <slot name="title" />
      </template>

      <!-- 列上方自定义内容 -->
      <slot name="columns-top" />

      <!-- 选择器列区域 -->
      <div
        :class="classes(
          n('content'),
          { [n('content--disabled')]: disabled }
        )"
      >
        <div
          :class="n('columns')"
          :style="columnsStyle"
        >
          <div :class="n('columns__bar')" />
          <div :class="classes(n('columns__mask'), n('columns__mask--top'))" />
          <div :class="classes(n('columns__mask'), n('columns__mask--bottom'))" />
          <div :class="n('columns__inner')">
            <PickerColumn
              v-for="(columnOptions, colIdx) in normalizedColumns"
              :key="`col-${colIdx}-${columnOptions.length}`"
              ref="columnRefs"
              :options="columnOptions"
              :value="internalValues[colIdx]"
              :column-index="colIdx"
              :visible-option-num="visibleOptionNum"
              :option-height="optionHeight"
              :swipe-duration="swipeDuration"
              :disabled="disabled"
              :columns-field-names="columnsFieldNames"
              @change="handleColumnChange"
            >
              <template
                v-if="$slots.option"
                #option="slotProps"
              >
                <slot
                  name="option"
                  v-bind="slotProps"
                />
              </template>
            </PickerColumn>
          </div>
        </div>
      </div>

      <!-- 列下方自定义内容 -->
      <slot name="columns-bottom" />

      <!-- 自定义确认按钮 -->
      <template
        v-if="$slots.confirm"
        #footer
      >
        <slot name="confirm" />
      </template>
    </HalfScreenDialog>
  </div>
</template>

<script lang="ts">
import {
  ref,
  computed,
  watch,
  defineComponent,
  isVue3,
} from 'virtual-vue';
import { props, DEFAULT_PICKER_OPTION_HEIGHT } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import HalfScreenDialog from '../half-screen-dialog/half-screen-dialog.vue';
import PickerColumnComponent from './components/picker-column.vue';
import { useColumns } from './use-columns';
import type { PickerOption, PickerColumnExpose } from './types';
import type { HalfScreenDialogAction } from '../half-screen-dialog/half-screen-dialog';

const { name, n, classes } = createNamespace('picker');

/** optionHeight 到 rem 的换算系数 */
const OPTION_HEIGHT_UNIT = 0.005;

export default defineComponent({
  name,
  components: {
    HalfScreenDialog,
    PickerColumn: PickerColumnComponent,
  },
  props,
  emits,
  setup(props, { emit }) {
    /** 子列组件实例引用数组（v-for 中收集） */
    const columnRefs = ref<PickerColumnExpose[]>([]);

    /**
     * 双向绑定 visible，供 HalfScreenDialog 使用
     * Vue3: v-model:visible  /  Vue2: :visible.sync
     */
    const currentVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => {
        emit('update:visible', value);
      },
    });

    // ==================== v-model 兼容层 ====================
    // Vue3 v-model 绑定 modelValue + update:modelValue
    // Vue2 v-model 绑定 value + input

    /** 读取当前 v-model 绑定的值，兼容 Vue2/3 */
    const getModelValue = (): (string | number)[] => (isVue3 ? props.modelValue : (props as Record<string, unknown>).value as (string | number)[]);

    /** 向外 emit v-model 值变更，同时触发 Vue3 和 Vue2 的事件 */
    const emitModelValue = (val: (string | number)[]) => {
      emit('update:modelValue', val);
      emit('input', val);
    };

    // ==================== 内部状态 ====================

    /** 内部选中值副本，与外部 v-model 通过 watch 同步 */
    const internalValues = ref<(string | number)[]>([...getModelValue()]);

    // ==================== 字段名解析工具 ====================

    /** 根据 columnsFieldNames 配置获取实际字段名（如 'text' → 'label'） */
    const getFieldName = (field: string): string => {
      const names = props.columnsFieldNames as Record<string, string | undefined>;
      return names?.[field] || field;
    };

    /** 从选项对象中读取指定字段的值，支持自定义字段名映射 */
    const getFieldValue = (option: PickerOption, field: string): string | number => {
      const customField = getFieldName(field);
      return option[customField] as string | number;
    };

    // ==================== columns 类型检测与标准化 ====================

    const { columnsType, normalizedColumns } = useColumns({
      columns: () => props.columns,
      internalValues,
      getFieldName,
      getFieldValue,
    });

    // ==================== HalfScreenDialog 确认按钮 ====================

    const columnsStyle = computed(() => {
      if (props.optionHeight === DEFAULT_PICKER_OPTION_HEIGHT) return undefined;
      return {
        '--wphk-design-picker-item-height': `${props.optionHeight * OPTION_HEIGHT_UNIT}rem`,
      };
    });

    /** 底部确认按钮配置，传给 HalfScreenDialog 的 actions */
    const confirmActions = computed<HalfScreenDialogAction[]>(() => [
      {
        key: 'confirm',
        text: props.confirmButtonText,
        type: 'primary' as const,
      },
    ]);

    // ==================== 选中信息收集 ====================

    /**
     * 根据 internalValues 和 normalizedColumns 汇总当前所有列的选中信息
     * 返回 selectedValues / selectedOptions / selectedIndexes，用于 confirm/cancel/change 事件
     */
    const getSelectedInfo = () => {
      const selectedValues: (string | number)[] = [];
      const selectedOptions: PickerOption[] = [];
      const selectedIndexes: number[] = [];

      normalizedColumns.value.forEach((columnOptions, colIndex) => {
        const currentValue = internalValues.value[colIndex];
        const optionIndex = columnOptions.findIndex(
          opt => getFieldValue(opt, 'value') === currentValue,
        );
        const idx = optionIndex >= 0 ? optionIndex : 0;
        const option = columnOptions[idx];

        selectedValues.push(option ? getFieldValue(option, 'value') : currentValue);
        selectedOptions.push(option || ({ text: '', value: '' } as PickerOption));
        selectedIndexes.push(idx);
      });

      return { selectedValues, selectedOptions, selectedIndexes };
    };

    // ==================== 事件处理 ====================

    /** 停止所有列的惯性滚动动画（确认前调用，防止取到过渡中的值） */
    const stopAllColumns = () => {
      if (Array.isArray(columnRefs.value)) {
        columnRefs.value.forEach((col) => {
          col?.stopMomentum?.();
        });
      }
    };

    /** 确认按钮点击：停止滚动 → 收集选中值 → emit → 关闭弹窗 */
    const handleConfirm = () => {
      stopAllColumns();
      const info = getSelectedInfo();
      emitModelValue(info.selectedValues);
      emit('confirm', info);
      currentVisible.value = false;
    };

    /** 取消（关闭按钮 / 遮罩层点击）：emit cancel → 关闭弹窗 */
    const handleCancel = () => {
      const info = getSelectedInfo();
      emit('cancel', info);
      currentVisible.value = false;
    };

    /** 遮罩层点击处理，根据 closeOnClickOverlay 决定是否关闭 */
    const handleOverlayClick = () => {
      if (props.closeOnClickOverlay) {
        handleCancel();
      }
    };

    /**
     * 某一列滚动选中值变化时触发
     * - 更新 internalValues 中对应列的值
     * - 级联模式下：沿 children 链重置后续列为各自第一项
     * - 非级联模式：补齐缺少的列值
     */
    const handleColumnChange = (params: {
      value: string | number;
      index: number;
      option: PickerOption;
      columnIndex: number;
    }) => {
      const { columnIndex, value } = params;
      const newValues = [...internalValues.value];
      newValues[columnIndex] = value;

      if (columnsType.value === 'cascade') {
        const childrenKey = getFieldName('children');
        let childrenOptions: PickerOption[] | undefined = params.option?.[childrenKey] as PickerOption[] | undefined;
        let depth = columnIndex + 1;
        while (childrenOptions && childrenOptions.length > 0) {
          const prevValue = internalValues.value[depth];
          const matched = childrenOptions.find(
            opt => getFieldValue(opt, 'value') === prevValue,
          );
          const selected = matched || childrenOptions[0];
          newValues[depth] = selected ? getFieldValue(selected, 'value') : '';
          childrenOptions = selected ? selected[childrenKey] as PickerOption[] | undefined : undefined;
          depth += 1;
        }
        newValues.length = depth;
      } else {
        while (newValues.length < normalizedColumns.value.length) {
          const colOptions = normalizedColumns.value[newValues.length];
          if (colOptions && colOptions.length > 0) {
            newValues.push(getFieldValue(colOptions[0], 'value'));
          } else {
            newValues.push('');
          }
        }
      }

      internalValues.value = newValues;

      const info = getSelectedInfo();
      emit('change', { ...info, columnIndex });
    };

    // ==================== 外部值 → 内部同步 ====================

    /** 外部 v-model 变化时同步到 internalValues */
    watch(
      getModelValue,
      (val) => {
        if (JSON.stringify(val) !== JSON.stringify(internalValues.value)) {
          internalValues.value = [...val];
        }
      },
    );

    /** 弹窗打开时，从外部 v-model 重新读取值（用户可能在关闭期间修改了绑定值） */
    watch(
      () => props.visible,
      (val) => {
        if (val) {
          internalValues.value = [...getModelValue()];
        }
      },
    );

    // ==================== 暴露给外部的方法 ====================

    /** 打开选择器弹窗 */
    const open = () => {
      emit('update:visible', true);
    };

    /** 关闭选择器弹窗 */
    const close = () => {
      emit('update:visible', false);
    };

    /** 等同于点击确认按钮 */
    const confirm = () => {
      handleConfirm();
    };

    /** 获取当前所有列的选中选项 */
    const getSelectedOptions = (): PickerOption[] => getSelectedInfo().selectedOptions;

    return {
      n,
      classes,
      currentVisible,
      normalizedColumns,
      internalValues,
      columnsStyle,
      confirmActions,
      columnRefs,
      handleConfirm,
      handleCancel,
      handleOverlayClick,
      handleColumnChange,
      open,
      close,
      confirm,
      getSelectedOptions,
    };
  },
});
</script>
